import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, collection, onSnapshot, getDocs, query, where, deleteDoc, addDoc } from 'firebase/firestore';
import { db, appId } from '../lib/firebase';
import { calculateTotals, generateNumber, formatCurrency } from '../lib/utils';
import { PRICE_DATABASE } from '../lib/constants';
import { 
  ArrowLeft, Save, Printer, Mail, Percent, CheckSquare, GripVertical, Trash2, Plus, 
  LayoutGrid, Wand2, Loader2, Edit3, Check, PenTool, CheckCircle2, HardHat, Hammer, 
  ChevronDown, FileCheck, Cloud, Euro, Library, Search, X, UserPlus, Paperclip 
} from 'lucide-react';
import Button from './ui/Button';
import { SignaturePad, EmailModal } from './ui/Modals';

// IMPORTATION POUR LE PDF
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Editor = ({ user, activeCompanyId, docId, type = 'quote', onBack, onSaveSuccess, onRedirect }) => {
  const [docInfo, setDocInfo] = useState({ 
    id: null, type: type, number: '', date: new Date().toLocaleDateString('fr-FR'), 
    status: 'Brouillon', signature: null, projectAddress: '' 
  });
  
  const [sections, setSections] = useState([]);
  const [client, setClient] = useState({});
  const [clientsList, setClientsList] = useState([]); 
  
  const [globalSettings, setGlobalSettings] = useState({ 
      downPaymentValue: 30, downPaymentType: 'percent', discountValue: 0, discountType: 'percent' 
  });
  
  const [companyInfo, setCompanyInfo] = useState({
      name: 'BatiFlow', address: 'Adresse', city: 'Ville', email: 'contact@batiflow.net', phone: '', logoUrl: null, defaultVat: 20
  });

  const [libraryItems, setLibraryItems] = useState([]);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [targetSectionIndex, setTargetSectionIndex] = useState(null); 
  const [librarySearch, setLibrarySearch] = useState('');
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: '', email: '', address: '', city: '' });
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState(null);

  const dataRef = useRef({ docInfo, sections, client, globalSettings });
  useEffect(() => { dataRef.current = { docInfo, sections, client, globalSettings }; }, [docInfo, sections, client, globalSettings]);

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    const unsubClients = onSnapshot(collection(db, 'companies', activeCompanyId, 'clients'), (snap) => setClientsList(snap.docs.map(d => ({id: d.id, ...d.data()}))));
    const unsubLibrary = onSnapshot(collection(db, 'artifacts', appId, 'users', user.uid, 'library'), (snap) => setLibraryItems(snap.docs.map(d => ({id: d.id, ...d.data()}))));
    
    const loadSettingsAndDoc = async () => {
        let prefVat = 20; let prefDownPayment = 30; let prefDownType = 'percent';
        try {
            const sSnap = await getDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'general'));
            if(sSnap.exists()) {
                const sData = sSnap.data();
                if(sData.company) {
                    setCompanyInfo(prev => ({ ...prev, ...sData.company, defaultVat: Number(sData.company.defaultVat) || 20 }));
                    prefVat = Number(sData.company.defaultVat) || 20;
                    if (sData.company.defaultDownPaymentValue) prefDownPayment = Number(sData.company.defaultDownPaymentValue);
                    if (sData.company.defaultDownPaymentType) prefDownType = sData.company.defaultDownPaymentType;
                }
            }
        } catch(e) { console.error(e); }

        if (docId) {
            const collectionName = type === 'quote' ? 'quotes' : 'invoices';
            const snap = await getDoc(doc(db, 'companies', activeCompanyId, collectionName, String(docId)));
            if (snap.exists()) {
                const data = snap.data();
                setDocInfo({ id: data.id, type: data.type, number: data.number, date: data.date, status: data.status, signature: data.signature, projectAddress: data.projectAddress || '' });
                setSections(data.sections || []);
                setClient(clientsList.find(c => c.name === data.clientName) || { name: data.clientName || '' });
            }
        } else {
            const newId = Date.now();
            const newNum = generateNumber(type);
            setDocInfo({ id: newId, type: type, number: newNum, date: new Date().toLocaleDateString('fr-FR'), status: 'Brouillon', signature: null, projectAddress: '', clientName: '', total: 0 });
            setGlobalSettings({ downPaymentValue: prefDownPayment, downPaymentType: prefDownType, discountValue: 0, discountType: 'percent' });
            await setDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(newId)), { id: newId, type, companyId: activeCompanyId, number: newNum, date: new Date().toLocaleDateString('fr-FR'), status: 'Brouillon' });
        }
    };
    loadSettingsAndDoc();

    const autoSaveInterval = setInterval(async () => {
        const currentData = dataRef.current;
        if (!user || !currentData.docInfo.id) return;
        try {
            setIsSaving(true);
            const totals = calculateTotals(currentData.sections, currentData.globalSettings);
            const collectionName = type === 'quote' ? 'quotes' : 'invoices';
            await setDoc(doc(db, 'companies', activeCompanyId, collectionName, String(currentData.docInfo.id)), { ...currentData.docInfo, clientName: currentData.client.name || '', total: totals.totalTTC, sections: currentData.sections });
            setLastAutoSave(new Date()); setIsSaving(false);
        } catch (error) { setIsSaving(false); }
    }, 10000);

    return () => { unsubClients(); unsubLibrary(); clearInterval(autoSaveInterval); };
  }, [user, docId, type]); // ICI : J'ai retiré le commentaire qui causait l'erreur

  const totals = calculateTotals(sections, globalSettings);

  // --- GÉNÉRATION DU PDF ---
  const generatePDF = () => {
    const doc = new jsPDF({ unit: 'pt' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let cursorY = margin;

    // Bande colorée en haut (header moderne)
    doc.setFillColor(37, 99, 235); // Bleu moderne
    doc.rect(0, 0, pageWidth, 8, 'F');

    cursorY += 20;

    // Header: logo + company info (layout horizontal amélioré)
    if (companyInfo.logoUrl) {
      try { 
        doc.addImage(companyInfo.logoUrl, 'PNG', margin, cursorY, 100, 50); 
      } catch (e) { 
        try { 
          doc.addImage(companyInfo.logoUrl, 'JPEG', margin, cursorY, 100, 50); 
        } catch (e2) { /* ignore */ } 
      }
    }

    const companyX = margin + (companyInfo.logoUrl ? 115 : 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(companyInfo.name || '', companyX, cursorY + 14);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const infoLines = [companyInfo.address, companyInfo.city, companyInfo.phone, companyInfo.email].filter(Boolean);
    infoLines.forEach((l, i) => doc.text(l, companyX, cursorY + 32 + i * 12));

    // Client box moderne (design carte)
    const clientBoxWidth = 220;
    const clientBoxX = pageWidth - margin - clientBoxWidth;
    
    // Ombre subtile
    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.05}));
    doc.roundedRect(clientBoxX + 2, cursorY + 2, clientBoxWidth, 75, 8, 8, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    // Box client avec bordure
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(clientBoxX, cursorY, clientBoxWidth, 75, 8, 8, 'FD');
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('CLIENT', clientBoxX + 14, cursorY + 16);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(client.name || '', clientBoxX + 14, cursorY + 32);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    const clientLines = [client.address || '', client.city || ''].filter(Boolean);
    clientLines.forEach((l, i) => doc.text(l, clientBoxX + 14, cursorY + 46 + i * 12));

    cursorY += 100;

    // Ligne de séparation élégante
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, cursorY, pageWidth - margin, cursorY);
    
    cursorY += 25;

    // Title moderne avec badge de couleur
    const titleText = type === 'quote' ? 'DEVIS' : 'FACTURE';
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(margin, cursorY - 8, 100, 26, 4, 4, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(titleText, margin + 50, cursorY + 8, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`N° ${docInfo.number}`, margin + 110, cursorY + 8);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Date : ${docInfo.date}`, pageWidth - margin, cursorY + 8, { align: 'right' });
    
    if (docInfo.projectAddress) {
      doc.text(`Chantier : ${docInfo.projectAddress}`, pageWidth - margin, cursorY + 22, { align: 'right' });
    }

    cursorY += 40;

    // Helper to format numbers for PDF in French style (no NBSP, use regular space)
    const pdfFormat = (value, withSymbol = true) => {
      try {
        const num = Number(value) || 0;
        const parts = num.toFixed(2).split('.');
        let intPart = parts[0];
        // insert regular spaces every 3 digits
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const formatted = `${intPart},${parts[1]}`;
        return withSymbol ? `${formatted} €` : formatted;
      } catch (e) { return String(value); }
    };

    // Table body
    const tableBody = [];
    sections.forEach(section => {
      tableBody.push([{ content: section.title.toUpperCase(), colSpan: 5, styles: { fillColor: [240,240,240], fontStyle: 'bold' } }]);
      section.items.forEach(item => {
        tableBody.push([
          { content: String(item.desc || ''), styles: { cellPadding: 4 } },
          { content: String(item.qty || ''), styles: { halign: 'center' } },
          { content: String(item.unit || ''), styles: { halign: 'center' } },
          { content: pdfFormat(item.price).replace('€','').trim(), styles: { halign: 'right' } },
          { content: pdfFormat((item.qty || 0) * (item.price || 0)).replace('€','').trim(), styles: { halign: 'right' } }
        ]);
      });
    });

    autoTable(doc, {
      startY: cursorY,
      head: [['Désignation', 'Qté', 'Unité', 'PU HT', 'Total HT']],
      body: tableBody,
      theme: 'striped',
      styles: { 
        fontSize: 9, 
        cellPadding: 8, 
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.5
      },
      headStyles: { 
        fillColor: [51, 65, 85],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: { 
        0: { cellWidth: 'auto' }, 
        1: { cellWidth: 45, halign: 'center' }, 
        2: { cellWidth: 45, halign: 'center' }, 
        3: { cellWidth: 80, halign: 'right' }, 
        4: { cellWidth: 80, halign: 'right', fontStyle: 'bold' } 
      },
      didDrawPage: (data) => {},
    });

    const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 20 : cursorY + 20;

    // Totals box moderne avec dégradé visuel
    const totalsBoxWidth = 240;
    const totalsX = pageWidth - margin - totalsBoxWidth;
    
    // Ombre du box
    doc.setFillColor(0, 0, 0);
    doc.setGState(new doc.GState({opacity: 0.08}));
    doc.roundedRect(totalsX + 3, finalY + 3, totalsBoxWidth, 85, 8, 8, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    // Box principal
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(1);
    doc.roundedRect(totalsX, finalY, totalsBoxWidth, 85, 8, 8, 'FD');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Total HT', totalsX + 16, finalY + 20);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.rawTotalHT), totalsX + totalsBoxWidth - 16, finalY + 20, { align: 'right' });
    
    doc.setTextColor(71, 85, 105);
    doc.text(`TVA (${companyInfo.defaultVat}%)`, totalsX + 16, finalY + 40);
    doc.setTextColor(30, 30, 30);
    doc.text(pdfFormat(totals.totalTVA), totalsX + totalsBoxWidth - 16, finalY + 40, { align: 'right' });
    
    // Ligne de séparation avant total
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1);
    doc.line(totalsX + 16, finalY + 52, totalsX + totalsBoxWidth - 16, finalY + 52);
    
    // Total TTC avec fond bleu
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(totalsX + 10, finalY + 60, totalsBoxWidth - 20, 18, 4, 4, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('NET À PAYER', totalsX + 16, finalY + 72);
    doc.setFontSize(13);
    doc.text(pdfFormat(totals.totalTTC), totalsX + totalsBoxWidth - 16, finalY + 72, { align: 'right' });

    // Signature area moderne: client + entreprise côte à côte
    const sigY = finalY + 110;
    const sigWidth = 230;
    const sigHeight = 75;
    const gap = 35;
    const leftX = margin;
    const rightX = margin + sigWidth + gap;

    // Client signature box moderne
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE CLIENT', leftX, sigY - 10);
    
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(leftX, sigY, sigWidth, sigHeight, 6, 6, 'FD');
    
    if (docInfo.signature) {
      try { 
        doc.addImage(docInfo.signature, 'PNG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
      } catch (e) { 
        try { 
          doc.addImage(docInfo.signature, 'JPEG', leftX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
        } catch (e2) { 
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', leftX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
        } 
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', leftX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
    }

    // Company signature box moderne
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('SIGNATURE ENTREPRISE', rightX, sigY - 10);
    
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(1.5);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(rightX, sigY, sigWidth, sigHeight, 6, 6, 'FD');
    
    const companySig = companyInfo.signatureUrl || companyInfo.signature || companyInfo.companySignature;
    if (companySig) {
      try { 
        doc.addImage(companySig, 'PNG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
      } catch (e) { 
        try { 
          doc.addImage(companySig, 'JPEG', rightX + 5, sigY + 5, sigWidth - 10, sigHeight - 10); 
        } catch (e2) { 
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(148, 163, 184);
          doc.text('En attente de signature', rightX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
        } 
      }
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(148, 163, 184);
      doc.text('En attente de signature', rightX + sigWidth/2, sigY + sigHeight/2, { align: 'center' });
    }

    // Legal footer moderne avec bordure haute
    const footerY = doc.internal.pageSize.getHeight() - 60;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    
    if (companyInfo.legalFooter) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const footerText = doc.splitTextToSize(companyInfo.legalFooter, pageWidth - margin * 2);
      doc.text(footerText, margin, footerY + 12);
    }
    
    // Bande colorée en bas
    doc.setFillColor(37, 99, 235);
    doc.rect(0, doc.internal.pageSize.getHeight() - 8, pageWidth, 8, 'F');

    const pdfOutput = doc.output('datauristring');
    return pdfOutput.split(',')[1];
  };           

  const handleEmailSent = async (emailData) => {
      setShowEmailModal(false);
      if (!client.email) { alert("Ce client n'a pas d'adresse email."); return; }
      try {
          const pdfBase64 = generatePDF();
          const pdfName = `${type === 'quote' ? 'Devis' : 'Facture'}_${docInfo.number}.pdf`;
          const attachments = [{ filename: pdfName, content: pdfBase64, encoding: 'base64' }];
          if (emailData.attachment) { attachments.push({ filename: emailData.attachment.filename, content: emailData.attachment.content, encoding: 'base64' }); }

          await addDoc(collection(db, 'mail'), {
              to: [client.email],
              bcc: [user.email], 
              message: {
                subject: emailData.subject || `${type === 'quote' ? 'Devis' : 'Facture'} ${docInfo.number} - ${companyInfo.name}`,
                html: `<div style="font-family: sans-serif; color: #333;"><p>${emailData.message.replace(/\n/g, '<br/>')}</p><br/><p>Vous trouverez le document <strong>${docInfo.number}</strong> en pièce jointe.</p><br/><p>Cordialement,<br><strong>${companyInfo.name}</strong></p></div>`,
                attachments: attachments
              }
          });

          if (docInfo.id && docInfo.status === 'Brouillon' && type === 'quote') {
              const newStatus = 'En attente';
              setDocInfo(prev => ({...prev, status: newStatus}));
              await setDoc(doc(db, 'companies', activeCompanyId, 'quotes', String(docInfo.id)), { ...docInfo, status: newStatus, clientName: client.name || '', total: totals.totalTTC, sections });
          }
          alert(`Email envoyé à ${client.email} avec le PDF joint !`);
      } catch (e) { console.error("Erreur mail:", e); alert("Erreur lors de l'envoi."); }
  };

  const handleSave = async () => { if (!user || !docInfo.id) return; setIsSaving(true); const totals = calculateTotals(sections, globalSettings); await setDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(docInfo.id)), { ...docInfo, clientName: client.name || '', total: totals.totalTTC, sections }); setLastAutoSave(new Date()); setIsSaving(false); if (onSaveSuccess) onSaveSuccess(); };
  const handleDelete = async () => { if (!docInfo.id) return; if (window.confirm("Supprimer définitivement ?")) { await deleteDoc(doc(db, 'companies', activeCompanyId, type === 'quote' ? 'quotes' : 'invoices', String(docInfo.id))); if (onBack) onBack(); } };
  const handleCreateClient = async () => { if (!newClientForm.name) return; const newId = Date.now(); await setDoc(doc(db, 'companies', activeCompanyId, 'clients', String(newId)), { id: newId, companyId: activeCompanyId, ...newClientForm }); setClient({ id: newId, ...newClientForm }); setShowNewClientModal(false); setShowClientSelector(false); setNewClientForm({ name: '', email: '', address: '', city: '' }); };
  const handleAddFromLibrary = (item) => { if (targetSectionIndex === null) return; const newSections = [...sections]; newSections[targetSectionIndex].items.push({ id: Date.now(), desc: item.desc, qty: 1, unit: item.unit || 'u', price: item.price || 0, nature: item.nature || 'labor', vat: item.vat !== undefined ? item.vat : companyInfo.defaultVat }); setSections(newSections); setShowLibraryModal(false); setLibrarySearch(''); };
  const handleAddManualItem = (sIdx) => { const newSections = [...sections]; newSections[sIdx].items.push({ id: Date.now(), desc: "Nouvel élément", qty: 1, unit: 'u', price: 0, nature: 'labor', vat: companyInfo.defaultVat }); setSections(newSections); };
  const handleDeleteItem = (sIdx, iIdx) => { const newSections = [...sections]; newSections[sIdx].items.splice(iIdx, 1); setSections(newSections); };
  const handleDeleteSection = (sIdx) => { if (window.confirm("Supprimer cette section ?")) { const newSections = [...sections]; newSections.splice(sIdx, 1); setSections(newSections); } };
  
  const onSectionDragStart = (e, index) => { setDraggedSectionIndex(index); e.dataTransfer.effectAllowed = 'move'; };
  const onSectionDragOver = (e, index) => { e.preventDefault(); if (draggedSectionIndex === null || draggedSectionIndex === index) return; const newSections = [...sections]; const [movedSection] = newSections.splice(draggedSectionIndex, 1); newSections.splice(index, 0, movedSection); setSections(newSections); setDraggedSectionIndex(index); };
  const onSectionDragEnd = () => { setDraggedSectionIndex(null); };
  const onItemDragStart = (e, sIdx, iIdx) => { e.stopPropagation(); setDraggedItem({ s: sIdx, i: iIdx }); e.dataTransfer.effectAllowed = 'move'; };
  const onItemDragOver = (e, targetS, targetI) => { e.preventDefault(); e.stopPropagation(); if (!draggedItem) return; if (draggedItem.s === targetS && draggedItem.i === targetI) return; const newSections = sections.map(sec => ({...sec, items: [...sec.items]})); const sourceList = newSections[draggedItem.s].items; const targetList = newSections[targetS].items; const [movedItem] = sourceList.splice(draggedItem.i, 1); targetList.splice(targetI, 0, movedItem); setSections(newSections); setDraggedItem({ s: targetS, i: targetI }); };
  const onItemDragEnd = () => { setDraggedItem(null); };
  const toggleNature = (sIdx, iIdx, nature) => { const newSections = [...sections]; newSections[sIdx].items[iIdx].nature = nature; setSections(newSections); };
  
  const filteredLibrary = libraryItems.filter(item => item.desc.toLowerCase().includes(librarySearch.toLowerCase()));
  const UNIT_OPTIONS = [
    { label: "QUANTITÉS", options: ["u", "pièce", "pc", "ens", "lot", "forfait"] },
    { label: "TEMPS", options: ["h", "heure", "j", "jour", "sem", "mois"] },
    { label: "LONGUEURS", options: ["m", "cm", "mm", "km", "ml"] },
    { label: "SURFACES", options: ["m²", "m2"] },
    { label: "VOLUMES", options: ["m³", "m3", "l", "litre", "cl"] },
    { label: "POIDS", options: ["kg", "g", "t", "tonne"] },
    { label: "CONDITIONNEMENTS", options: ["sac", "carton", "palette", "rouleau", "bidon"] }
  ];
  const statusOptions = type === 'quote' ? ['Brouillon', 'En attente', 'Signé', 'Refusé'] : ['Brouillon', 'En attente', 'Payée', 'En retard'];
  const getStatusColor = (status) => { switch(status) { case 'Signé': case 'Payée': return 'bg-green-100 text-green-700 border-green-200'; case 'En attente': return 'bg-orange-100 text-orange-700 border-orange-200'; case 'Refusé': case 'En retard': return 'bg-red-100 text-red-700 border-red-200'; default: return 'bg-slate-100 text-slate-500 border-slate-200'; } };
  
  const convertToInvoice = async (invoiceType) => { 
    if (!docInfo.id) return; 
    
    // Sauvegarder le devis actuel avant de créer la facture
    await handleSave();
    
    let invoiceSections = JSON.parse(JSON.stringify(sections)); 
    let invoiceTotal = totals.totalTTC; 
    
    if (invoiceType === 'deposit') { 
      // Facture d'acompte : calculer le montant selon le %
      const depositPercent = globalSettings.downPaymentValue || 30;
      invoiceTotal = totals.totalTTC * (depositPercent / 100); 
      const priceHT = invoiceTotal / (1 + (companyInfo.defaultVat / 100));
      invoiceSections = [{ 
        id: Date.now(), 
        title: "Acompte", 
        items: [{ 
          id: Date.now(), 
          desc: `Acompte ${depositPercent}% sur devis ${docInfo.number}`, 
          qty: 1, 
          unit: 'u', 
          price: priceHT, 
          vat: companyInfo.defaultVat 
        }] 
      }]; 
    } 
    
    const newInvoiceId = Date.now(); 
    const invoiceData = { 
      id: newInvoiceId, 
      type: 'invoice', 
      number: generateNumber('invoice'), 
      clientName: client.name || '', 
      date: new Date().toLocaleDateString('fr-FR'), 
      status: 'Brouillon', 
      sections: invoiceSections, 
      total: invoiceTotal, 
      sourceQuote: docInfo.number, 
      projectAddress: docInfo.projectAddress,
      companyId: activeCompanyId
    }; 
    
    console.log('[Editor] Creating invoice:', invoiceType, invoiceData);
    await setDoc(doc(db, 'companies', activeCompanyId, 'invoices', String(newInvoiceId)), invoiceData); 
    
    // Rediriger vers l'éditeur de facture
    if(onRedirect) {
      onRedirect(newInvoiceId, 'invoice');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-2 md:p-6 animate-page pb-32">
      {/* MODALE NEW CLIENT */}
      {showNewClientModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in zoom-in-95" onClick={() => setShowNewClientModal(false)}>
              <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><UserPlus size={20} className="text-blue-600"/> Nouveau Client Rapide</h3>
                  <div className="space-y-3">
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Nom / Entreprise *" value={newClientForm.name} onChange={e => setNewClientForm({...newClientForm, name: e.target.value})} autoFocus />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Email" value={newClientForm.email} onChange={e => setNewClientForm({...newClientForm, email: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Adresse" value={newClientForm.address} onChange={e => setNewClientForm({...newClientForm, address: e.target.value})} />
                      <input className="w-full border rounded-lg p-2 text-sm" placeholder="Ville" value={newClientForm.city} onChange={e => setNewClientForm({...newClientForm, city: e.target.value})} />
                  </div>
                  <div className="flex justify-end gap-2 mt-4"><Button variant="secondary" onClick={() => setShowNewClientModal(false)}>Annuler</Button><Button variant="primary" onClick={handleCreateClient} disabled={!newClientForm.name}>Créer et Sélectionner</Button></div>
              </div>
          </div>
      )}

      {/* MODALE BIBLIOTHEQUE */}
      {showLibraryModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in zoom-in-95" onClick={() => setShowLibraryModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50"><h3 className="font-bold text-slate-800 flex items-center gap-2"><Library size={20} className="text-indigo-500"/> Bibliothèque de Prix</h3><button onClick={() => setShowLibraryModal(false)}><X size={20} className="text-slate-400 hover:text-slate-600"/></button></div>
                  <div className="p-4 border-b border-slate-100"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16}/><input className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Rechercher un article..." value={librarySearch} onChange={e => setLibrarySearch(e.target.value)} autoFocus /></div></div>
                  <div className="flex-1 overflow-y-auto p-2">
                      {filteredLibrary.length === 0 ? <div className="text-center p-8 text-slate-400 text-sm">Aucun article trouvé.</div> : <div className="grid gap-2">{filteredLibrary.map(item => (<div key={item.id} onClick={() => handleAddFromLibrary(item)} className="flex items-center justify-between p-3 hover:bg-indigo-50 rounded-xl cursor-pointer border border-transparent hover:border-indigo-100 transition group"><div className="flex items-center gap-3"><div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${item.nature === 'labor' ? 'bg-blue-50 border-blue-100 text-blue-600' : 'bg-amber-50 border-amber-100 text-amber-600'}`}>{item.nature === 'labor' ? <HardHat size={14}/> : <Hammer size={14}/>}</div><div><div className="font-bold text-sm text-slate-700">{item.desc}</div><div className="text-xs text-slate-400">{item.unit}</div></div></div><div className="font-mono font-bold text-indigo-600">{formatCurrency(item.price)}</div></div>))}</div>}
                  </div>
              </div>
          </div>
      )}

      {/* Barre d'outils */}
      <div className="flex flex-col gap-4 mb-6 print:hidden">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium transition hover:bg-slate-100 px-3 py-2 rounded-lg w-fit"><ArrowLeft size={18}/> Retour</button>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-100 shadow-sm">{isSaving ? <><Loader2 size={12} className="animate-spin text-blue-500"/> Sauvegarde...</> : lastAutoSave ? <><Cloud size={12} className="text-green-500"/> Sauvegardé à {lastAutoSave.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</> : <><Cloud size={12}/> Prêt</>}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {type === 'quote' && docInfo.id && (<><Button variant="secondary" onClick={() => convertToInvoice('deposit')} icon={Percent} className="whitespace-nowrap">Facture Acompte</Button><Button variant="blue" onClick={() => convertToInvoice('final')} icon={FileCheck} className="whitespace-nowrap">Facture Solde</Button><div className="hidden md:block w-px h-8 bg-slate-200 mx-1"></div></>)}
          <Button variant="secondary" onClick={() => window.print()} icon={Printer} className="whitespace-nowrap">Imprimer</Button>
          <Button variant="primary" onClick={handleSave} icon={Save} className="whitespace-nowrap">Sauvegarder</Button>
          <Button variant="secondary" onClick={() => setShowEmailModal(true)} icon={Mail} className="whitespace-nowrap">Envoyer</Button>
          <Button variant="danger" onClick={handleDelete} icon={Trash2} className="whitespace-nowrap">Supprimer</Button>
        </div>
      </div>

      <div className="bg-white shadow-xl md:shadow-2xl md:rounded-3xl min-h-[600px] md:min-h-[900px] p-4 md:p-12 relative print:shadow-none print:p-0">
        <div className="flex flex-col md:flex-row justify-between mb-8 md:mb-16 border-b border-slate-100 pb-8 gap-6">
          <div className="flex items-start gap-4">
            {companyInfo.logoUrl && <img src={companyInfo.logoUrl} alt="Logo" className="w-20 h-20 object-contain rounded-lg border border-slate-100" />}
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tighter mb-2">{companyInfo.name}</h1>
              <div className="text-slate-500 text-sm font-medium space-y-1">
                <p>{companyInfo.address}</p><p>{companyInfo.city}</p><p className="text-blue-600">{companyInfo.email}</p><p>{companyInfo.phone}</p>
              </div>
            </div>
          </div>
          <div className="w-full md:w-72">
            <div className="bg-slate-50 p-4 md:p-5 rounded-xl md:rounded-2xl border border-slate-100 cursor-pointer hover:border-blue-200 transition relative" onClick={() => setShowClientSelector(!showClientSelector)}>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Client</div><div className="font-bold text-slate-800 text-lg mb-1">{client.name || 'Sélectionner...'}</div><div className="text-sm text-slate-500">{client.address} {client.city}</div><div className="absolute top-3 right-3 text-blue-400"><Edit3 size={14}/></div>
              {showClientSelector && (<div className="absolute top-full right-0 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-10 max-h-60 overflow-y-auto"><div className="p-3 hover:bg-green-50 text-green-700 font-bold text-sm border-b border-slate-50 cursor-pointer flex items-center gap-2" onClick={(e) => { e.stopPropagation(); setShowNewClientModal(true); }}><Plus size={16}/> Nouveau Client</div>{clientsList.map(c => (<div key={c.id} className="p-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0" onClick={(e) => { e.stopPropagation(); setClient(c); setShowClientSelector(false); }}><div className="font-bold text-slate-800 text-sm">{c.name}</div></div>))}</div>)}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-4">
          <div className="w-full md:w-1/2">
            {isEditingAddress ? (<div className="flex gap-2 items-start"><textarea className="w-full bg-slate-50 border border-blue-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none" rows="3" value={docInfo.projectAddress} onChange={e => setDocInfo({...docInfo, projectAddress: e.target.value})}/><button onClick={() => setIsEditingAddress(false)} className="bg-green-500 text-white p-2 rounded-lg"><Check size={16}/></button></div>) : (<div onClick={() => setIsEditingAddress(true)} className="group cursor-pointer"><div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">Lieu des travaux <Edit3 size={12}/></div><div className="text-sm text-slate-700 font-medium bg-slate-50 p-3 rounded-xl border border-transparent hover:border-slate-200 transition">{docInfo.projectAddress || "Adresse identique à la facturation"}</div></div>)}
          </div>
          <div className="text-left md:text-right w-full md:w-auto">
            <div className="text-2xl md:text-4xl font-bold text-slate-900 mb-1">{docInfo.number}</div><div className="text-slate-400 font-medium text-sm mb-3">Émis le {docInfo.date}</div>
            <div className="relative inline-block"><select value={docInfo.status} onChange={(e) => setDocInfo({...docInfo, status: e.target.value})} className={`appearance-none pl-4 pr-8 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border cursor-pointer transition shadow-sm outline-none ${getStatusColor(docInfo.status)}`}>{statusOptions.map(s => <option key={s} value={s} className="bg-white text-slate-800">{s}</option>)}</select><div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-current opacity-50"><ChevronDown size={14} /></div></div>
          </div>
        </div>

        {/* --- SECTIONS DU DEVIS --- */}
        <div className="mb-12">
          <div className="hidden md:grid grid-cols-12 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-widest py-4 px-6 rounded-t-2xl"><div className="col-span-5">Désignation</div><div className="col-span-2 text-center">Qté / Unité</div><div className="col-span-2 text-right">PU HT</div><div className="col-span-1 text-right">Total HT</div><div className="col-span-2 text-right">Total TTC</div></div>
          <div className="border-t-0 md:border border-slate-200 rounded-b-2xl">
            {sections.map((section, sIdx) => (
              <div key={section.id || sIdx} className="border-b border-slate-200 last:border-0">
                <div className="bg-slate-100 md:bg-slate-50 px-4 md:px-6 py-3 flex justify-between items-center cursor-move group">
                  <div className="flex items-center gap-2 flex-1"><GripVertical size={14} className="text-slate-300 group-hover:text-slate-500"/><input className="bg-transparent outline-none w-full font-bold text-slate-700 uppercase tracking-wide text-xs" value={section.title} onChange={e => {const ns = [...sections]; ns[sIdx].title = e.target.value; setSections(ns);}} placeholder="TITRE DE LA SECTION"/></div>
                  <button onClick={() => handleDeleteSection(sIdx)} className="text-slate-400 hover:text-red-500 transition"><Trash2 size={14}/></button>
                </div>
                
                {section.items.map((item, iIdx) => {
                  const totalHT = item.qty * item.price;
                  const totalTTC = totalHT * (1 + companyInfo.defaultVat / 100);
                  
                  // Filtrer les unités selon le type
                  // labor = temps uniquement, material = tout sauf temps
                  const filteredUnits = item.nature === 'labor' 
                    ? UNIT_OPTIONS.filter(group => group.label === 'TEMPS')
                    : item.nature === 'material'
                    ? UNIT_OPTIONS.filter(group => group.label !== 'TEMPS')
                    : UNIT_OPTIONS;
                  
                  return (
                  <div key={item.id || iIdx} className="flex flex-col md:grid md:grid-cols-12 px-4 md:px-6 py-4 text-sm items-start bg-white border-b border-slate-100 last:border-0 hover:bg-blue-50/20 transition group">
                    <div className="w-full md:col-span-5 md:pr-4 mb-3 md:mb-0 relative">
                      <textarea rows={1} className="w-full bg-transparent outline-none resize-none overflow-hidden font-medium text-slate-700" value={item.desc} onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].desc=e.target.value;setSections(ns);e.target.style.height='auto';e.target.style.height=e.target.scrollHeight+'px'}}/>
                      <div className="flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => toggleNature(sIdx, iIdx, 'labor')} className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold border ${item.nature === 'labor' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-slate-400'}`}>Main d'oeuvre</button><button onClick={() => toggleNature(sIdx, iIdx, 'material')} className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold border ${item.nature === 'material' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-slate-400'}`}>Matériel</button></div>
                    </div>
                    <div className="flex flex-wrap md:contents gap-2 w-full items-center mt-2 md:mt-0">
                        <div className="flex items-center gap-2 md:col-span-2 md:justify-center">
                          <input className="w-14 text-center bg-slate-100 rounded-md py-1.5 outline-none font-medium text-slate-700" value={item.qty} onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].qty=Number(e.target.value);setSections(ns)}} />
                          <select 
                            className="w-20 text-center bg-white border border-slate-300 rounded-md py-1.5 text-xs font-medium text-slate-600 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-slate-400 transition"
                            value={item.unit} 
                            onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].unit=e.target.value;setSections(ns)}}
                          >
                            <option value="">Unité</option>
                            {filteredUnits.map(group => (
                              <optgroup key={group.label} label={group.label}>
                                {group.options.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-1 md:col-span-2 md:justify-end"><span className="md:hidden text-xs text-slate-400">Prix HT:</span><div className="flex items-center gap-1"><input className="w-16 md:w-20 text-right bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 outline-none" value={item.price} onChange={e => {const ns=[...sections];ns[sIdx].items[iIdx].price=Number(e.target.value);setSections(ns)}} /><span className="text-slate-600">€</span></div></div>
                        <div className="md:col-span-1 text-right text-slate-600 font-medium text-xs">{formatCurrency(totalHT)}</div>
                        <div className="md:col-span-2 text-right font-bold text-slate-900 flex items-center justify-end gap-3">{formatCurrency(totalTTC)}<button onClick={() => handleDeleteItem(sIdx, iIdx)} className="text-slate-300 hover:text-red-500 transition"><Trash2 size={14}/></button></div>
                    </div>
                  </div>
                  );
                })}
                <div className="px-6 py-3 bg-slate-50/50 flex items-center gap-4"><button onClick={() => handleAddManualItem(sIdx)} className="text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline"><Plus size={14}/> Ajouter ligne</button><button onClick={() => { setTargetSectionIndex(sIdx); setShowLibraryModal(true); }} className="text-xs text-indigo-600 font-bold flex items-center gap-1 hover:underline"><Library size={14}/> Catalogue</button></div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-center"><button onClick={() => setSections([...sections, {id: Date.now(), title: "Nouvelle Section", items: []}])} className="text-xs bg-slate-800 text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 font-bold uppercase"><LayoutGrid size={14}/> Ajouter une Section</button></div>
        </div>

        <div className="flex flex-col md:flex-row justify-end border-t border-slate-100 pt-8 gap-8">
          <div className="w-full md:w-1/3 space-y-6">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-slate-500"><span>Total HT</span> <span className="font-medium">{formatCurrency(totals.rawTotalHT)}</span></div>
              <div className="flex justify-between items-center text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                <span className="font-medium">TVA</span>
                <div className="flex items-center gap-2">
                  <select 
                    className="bg-white border border-slate-300 rounded-md px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    value={companyInfo.defaultVat} 
                    onChange={(e) => setCompanyInfo({...companyInfo, defaultVat: Number(e.target.value)})}
                  >
                    <option value="0">0%</option>
                    <option value="5.5">5,5%</option>
                    <option value="10">10%</option>
                    <option value="20">20%</option>
                  </select>
                  <span className="font-medium">{formatCurrency(totals.totalTVA)}</span>
                </div>
              </div>
              <div className="flex justify-between text-2xl font-extrabold text-slate-900 pt-4 border-t border-slate-200"><span>Net à payer</span> <span>{formatCurrency(totals.totalTTC)}</span></div>
            </div>
            {type === 'quote' && (<div className="p-4 bg-blue-50 rounded-lg border border-blue-100 shadow-sm print:border-none print:shadow-none print:p-0 print:bg-transparent mt-4"><div className="flex justify-between items-center mb-2 text-blue-800 text-sm font-bold"><span className="flex items-center gap-2"><Percent size={16}/> Acompte requis</span><div className="flex items-center bg-white border border-blue-200 rounded-lg overflow-hidden w-32 print:border-none"><input type="number" className="w-full outline-none text-right font-bold text-blue-900 px-2 py-1.5 bg-transparent text-sm" value={globalSettings.downPaymentValue} onChange={(e) => setGlobalSettings({...globalSettings, downPaymentValue: Number(e.target.value)})} /><select className="bg-white text-blue-600 font-bold text-xs py-0.5 px-1 outline-none border-l border-blue-100 cursor-pointer appearance-none ml-1" value={globalSettings.downPaymentType} onChange={(e) => setGlobalSettings({...globalSettings, downPaymentType: e.target.value})}><option value="percent">%</option><option value="fixed">€</option></select></div></div><div className="flex justify-between items-center text-sm border-t border-blue-100 pt-2 mt-1"><span className="text-blue-600">Montant à régler :</span><span className="font-mono font-bold text-blue-900 text-lg">{formatCurrency(globalSettings.downPaymentType === 'percent' ? totals.totalTTC * (globalSettings.downPaymentValue / 100) : globalSettings.downPaymentValue)}</span></div></div>)}
            <div className="mt-8 pt-8 border-t border-slate-100">{docInfo.signature ? (<div className="text-center p-6 bg-green-50 border border-green-100 rounded-xl relative overflow-hidden"><div className="absolute top-0 right-0 p-2"><CheckCircle2 className="text-green-200" size={40}/></div><div className="text-[10px] font-bold text-green-700 uppercase mb-3 tracking-widest">Bon pour accord</div><img src={docInfo.signature} className="h-20 mx-auto opacity-90 mix-blend-multiply" alt="Signature" /><div className="text-[10px] text-green-600 mt-3 font-medium">Signé électroniquement le {new Date().toLocaleDateString()}</div></div>) : type === 'quote' && (<button onClick={() => setShowSignatureModal(true)} className="w-full py-6 border-2 border-dashed border-slate-300 rounded-xl text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition flex flex-col items-center justify-center gap-3 group print:hidden"><div className="bg-slate-100 p-3 rounded-full group-hover:bg-blue-100 transition"><PenTool size={24}/></div><span className="font-bold text-sm">Cliquez pour signer</span></button>)}</div>
          </div>
        </div>
      </div>
      {showSignatureModal && <SignaturePad onSave={(url) => { setDocInfo({...docInfo, signature: url, status: 'Signé'}); setShowSignatureModal(false); }} onCancel={() => setShowSignatureModal(false)} />}
      {showEmailModal && <EmailModal client={client} docNumber={docInfo.number} onClose={() => setShowEmailModal(false)} onSend={handleEmailSent} />}
    </div>
  );
};

export default Editor;